import React, { useEffect, useRef, useState } from "react";
import "./DeleteModal.css";
import { Box } from "@mui/material";
import Title from "../title/Title";
import {
  PrimaryButton,
  SecondaryButton,
} from "../buttonComponent/ButtonComponent";
import UserIcon from "../../image/user.svg";
import ModalCloseButton from "./ModalCloseButton";
import { useSnackbar } from "../Snackbar/SnackbarProvider";
import TextInput from "../textInput/TextInput";
import Checkbox from "../checkbox/Checkbox";
import Cookies from "js-cookie";
import { useAuth } from "../../config/utilities/useAuth";
import { formatCapitalize } from "../../config/utilities/formatCapitalize";
import {
  addPlusPhone,
  removePlusPhone,
} from "../../config/utilities/formatPhone";
import CustomPhoneInput from "../customPhoneInput/CustomPhoneInput";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  APIGetAdministratorByFirebaseToken,
  APIUpdateAdministrators,
} from "../../config/utilities/APIs";
import LoadingSpinner from "../loading/LoadingSpinner";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  width: "min(60rem, calc(100vw - 2rem))",
  overflow: "hidden",
  maxHeight: "92vh",
};

export default function ProfileModal({ isOpen, onClose }) {
  const { showSuccess, showError } = useSnackbar();
  const privilegesWithCheckboxes = [
    {
      name: "Live Module",
      checkboxes: [
        { key: "mapOverview", label: "Map Overview" },
        { key: "dispatchBoard", label: "Dispatch Board" },
      ],
    },
    {
      name: "Fleet Management Module",
      checkboxes: [
        { key: "vehicles", label: "Vehicles" },
        { key: "drivers", label: "Drivers" },
      ],
    },
    {
      name: "Manage Users Module",
      checkboxes: [
        { key: "administrators", label: "Administrators" },
        { key: "familyManagement", label: "Family Management" },
      ],
    },
    {
      name: "Schedule Management Module",
      checkboxes: [{ key: "routeManagement", label: "Route Management" }],
    },
    {
      name: "Notifications Campaign Module",
      checkboxes: [{ key: "campaignManagement", label: "Campaign Management" }],
    },
  ];

  const [keyUp, setKeyUp] = useState("", false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);

  const { privilege, token } = useAuth();
  const modules = privilege?.modules;
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;

  const [initialInput, setInitialInput] = useState(null);
  const [input, setInput] = useState({
    userId: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    role: "",
    privileges: [],
  });

  const [error, setError] = useState({
    firstName: false,
    lastName: false,
    phoneNumber: false,
    email: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const adminDetails = await APIGetAdministratorByFirebaseToken(
          token,
          setLoading
        );

        const formattedInput = {
          userId: adminDetails.userId,
          firstName: adminDetails.firstName,
          lastName: adminDetails.lastName,
          email: adminDetails.email,
          phoneNumber: addPlusPhone(adminDetails.phoneNumber),
          role: role,
          privileges: modules,
        };
        setInitialInput(formattedInput);
        setInput(formattedInput);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
        setIsDataFetched(true);
      }
    };

    fetchData();
  }, [token, modules, isOpen]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [isOpen]);

  const handleInputChange = (e, fieldName) => {
    if (fieldName) {
      // Handling phone input (which provides a string)
      setInput((prev) => ({ ...prev, [fieldName]: e || "" }));
    } else if (e && e.target) {
      // Handling normal text inputs
      const { name, value } = e.target;
      setInput((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEditProfile = async () => {
    setLoading(true);

    const formattedPhone = removePlusPhone(input.phoneNumber);
    const userId = input.userId;

    const adminData = {
      firstName: input.firstName,
      lastName: input.lastName,
      phoneNumber: formattedPhone,
    };

    try {
      const response = await APIUpdateAdministrators(
        userId,
        setLoading,
        adminData,
        institutionId
      );

      if (response?.status === 200) {
        // Update cookies with the new name
        updateCookies(adminData);

        // Show success message
        showSuccess("Update Successful", "Profile has been updated successfully");

        // Close modal only on success
        onClose();
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to update profile"
        );
      }
    } catch (error) {
      // Show error message
      showError("Update Failed", error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Update cookies function
  const updateCookies = ({ firstName, lastName }) => {
    const currentName = Cookies.get("name");
    const newName = `${firstName} ${lastName}`;

    if (currentName !== newName) {
      Cookies.set("name", newName);
      window.dispatchEvent(new Event("cookieUpdate"));
    }
  };

  const isFormValid = () => {
    let isValid = true;

    // Regular expressions
    const nameRegex = /^[A-Za-z\s]+$/;

    // Validate fields
    const newErrors = {
      firstName:
        input.firstName.trim() !== "" && !nameRegex.test(input.firstName),
      lastName: input.lastName.trim() !== "" && !nameRegex.test(input.lastName),
      phoneNumber:
        input.phoneNumber?.trim() === "" ||
        !isValidPhoneNumber(input.phoneNumber),
    };

    // Determine overall validity
    isValid =
      !Object.values(newErrors).includes(true) &&
      input.firstName.trim() !== "" &&
      input.lastName.trim() !== "" &&
      input.email.trim() !== "";
    input.phoneNumber.trim() !== "";

    // Check if input is edited
    const isEdited =
      initialInput && JSON.stringify(initialInput) !== JSON.stringify(input);

    setError(newErrors);

    return isValid && isEdited; // Ensure form is valid and fields have been changed
  };

  useEffect(() => {
    setIsSubmitDisabled(!isFormValid());
  }, [input, initialInput]);

  if (!isDataFetched) {
    return null
  }

  return (
    <div className="modal-backdrop">
      <Box sx={style} className="profile-modal-box">
        <div className="user-modal-header">
          <div className="user-modal-header-contents">
            <div className="icon-button-container">
              <div className="icon-container">
                <img
                  style={{ margin: "auto" }}
                  src={UserIcon}
                  alt="invite-users-icon"
                  loading="lazy"
                />
              </div>
              <div className="x-button-container">
                <ModalCloseButton onClick={onClose} className="user-x-button" />
              </div>
            </div>
            <div className="user-modal-header-title">
              <Title
                title="Manage Profile"
                subtitle="Manage the details of your BusBuddy account"
                titleSize="1.125rem"
                subtitleSize="0.875rem"
                gap="0.25rem"
                lineHeight="1.75rem"
              />
            </div>
          </div>
        </div>
        <div
          ref={contentRef}
          className={`user-modal-content ${
            isOverflowing ? "has-overflow" : ""
          }`}
        >
          <div className="name-container">
            <div className="user-modal-text">Name</div>
            <div className="invite-name-container">
              <div className="input-container">
                <TextInput
                  label="First Name"
                  placeholder="Enter first name"
                  setInput={setInput}
                  input={input}
                  name="firstName"
                  type="text"
                  error={error.firstName}
                  setError={(value) => setError({ ...error, firstName: value })}
                  errorMessage={"Enter Valid Name"}
                  onChangeFunction={handleInputChange}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                />
              </div>
              <div className="input-container">
                <TextInput
                  label="Last Name"
                  placeholder="Enter last name"
                  setInput={setInput}
                  input={input}
                  name="lastName"
                  type="text"
                  error={error.lastName}
                  setError={(value) => setError({ ...error, lastName: value })}
                  errorMessage={"Enter Valid Name"}
                  onChangeFunction={handleInputChange}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                />
              </div>
            </div>
          </div>

          <div className="email-container">
            <div className="user-modal-text">Email Address</div>
            <div className="invite-text-input-container">
              <TextInput
                placeholder={input.email}
                setInput={setInput}
                input=""
                name="email"
                type="text"
                error={error.email}
                setError={(value) => setError({ ...error, email: value })}
                errorMessage={"Enter Valid Email"}
                onChangeFunction={handleInputChange}
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                disable={true}
              />
            </div>
          </div>
          <div className="phone-container">
            <div className="user-modal-text">Phone Number</div>
            <div className="invite-text-input-container">
              <CustomPhoneInput
                value={input.phoneNumber || ""}
                onChange={(value) => handleInputChange(value, "phoneNumber")} // Pass fieldName explicitly
                error={error.phoneNumber}
                errorMessage="Enter valid phone number"
              />
            </div>
          </div>
          <div className="access-privilege-container">
            <div className="user-modal-text">Access Privilege</div>
            <div className="invite-text-input-container">
              <TextInput
                placeholder={formatCapitalize(input.role)}
                setInput={setInput}
                input=""
                name="role"
                type="text"
                error={error.role}
                setError={(value) => setError({ ...error, role: value })}
                errorMessage={"Enter Valid Role"}
                onChangeFunction={handleInputChange}
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                disable={true}
              />
            </div>
          </div>
          {["EDITOR", "VIEWER"].includes(input.role) && (
            <div className="selected-modules-container">
              <div className="user-modal-text">Selected Modules</div>
              <div className="modules-grid">
                {privilegesWithCheckboxes.map((module, index) => (
                  <div className="module-item" key={index}>
                    <div className="module-title">{module.name}</div>
                    <div className="checkbox-group">
                      {module.checkboxes.map((checkbox, checkboxIndex) => (
                        <Checkbox
                          disabled={true}
                          key={checkboxIndex}
                          value={checkbox.key} // Use key
                          text={checkbox.label} // Use label
                          width="16px"
                          height="16px"
                          checked={input.privileges[checkbox.key] || false}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="button-container-delete">
          <SecondaryButton
            width="12rem"
            label="Cancel"
            imageIcon={false}
            onClickFunction={onClose}
          />
          <PrimaryButton
            width="12rem"
            label="Save"
            onClickFunction={handleEditProfile}
            disable={isSubmitDisabled}
            loading={loading}
          />
        </div>
      </Box>
    </div>
  );
}
